import { Box, Tooltip } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { listAvailableBooks } from './rv1960-stub';

// ─── Reference patterns ────────────────────────────────────────────────────
// Supports Spanish full names plus common abbreviations. The chapter / verse
// portion is required (avoids false positives like "Juan dijo").

const BOOK_NAMES = [
  // Old Testament
  'Génesis',
  'Genesis',
  'Éxodo',
  'Exodo',
  'Levítico',
  'Levitico',
  'Números',
  'Numeros',
  'Deuteronomio',
  'Josué',
  'Josue',
  'Jueces',
  'Rut',
  '1 Samuel',
  '2 Samuel',
  '1 Reyes',
  '2 Reyes',
  '1 Crónicas',
  '2 Crónicas',
  'Esdras',
  'Nehemías',
  'Nehemias',
  'Ester',
  'Job',
  'Salmos',
  'Salmo',
  'Proverbios',
  'Eclesiastés',
  'Eclesiastes',
  'Cantares',
  'Isaías',
  'Isaias',
  'Jeremías',
  'Jeremias',
  'Lamentaciones',
  'Ezequiel',
  'Daniel',
  'Oseas',
  'Joel',
  'Amós',
  'Amos',
  'Abdías',
  'Abdias',
  'Jonás',
  'Jonas',
  'Miqueas',
  'Nahúm',
  'Nahum',
  'Habacuc',
  'Sofonías',
  'Sofonias',
  'Hageo',
  'Zacarías',
  'Zacarias',
  'Malaquías',
  'Malaquias',
  // New Testament
  'Mateo',
  'Marcos',
  'Lucas',
  'Juan',
  'Hechos',
  'Romanos',
  '1 Corintios',
  '2 Corintios',
  'Gálatas',
  'Galatas',
  'Efesios',
  'Filipenses',
  'Colosenses',
  '1 Tesalonicenses',
  '2 Tesalonicenses',
  '1 Timoteo',
  '2 Timoteo',
  'Tito',
  'Filemón',
  'Filemon',
  'Hebreos',
  'Santiago',
  '1 Pedro',
  '2 Pedro',
  '1 Juan',
  '2 Juan',
  '3 Juan',
  'Judas',
  'Apocalipsis',
];

// Common abbreviations → canonical name
const ABBREVIATIONS: Record<string, string> = {
  Gn: 'Génesis',
  Ex: 'Éxodo',
  Lv: 'Levítico',
  Nm: 'Números',
  Dt: 'Deuteronomio',
  Sal: 'Salmos',
  Pr: 'Proverbios',
  Ecl: 'Eclesiastés',
  Is: 'Isaías',
  Jer: 'Jeremías',
  Ez: 'Ezequiel',
  Dn: 'Daniel',
  Mt: 'Mateo',
  Mc: 'Marcos',
  Lc: 'Lucas',
  Jn: 'Juan',
  Hch: 'Hechos',
  Rom: 'Romanos',
  '1Co': '1 Corintios',
  '2Co': '2 Corintios',
  Gal: 'Gálatas',
  Ef: 'Efesios',
  Fil: 'Filipenses',
  Col: 'Colosenses',
  '1Tes': '1 Tesalonicenses',
  '2Tes': '2 Tesalonicenses',
  '1Tim': '1 Timoteo',
  '2Tim': '2 Timoteo',
  Heb: 'Hebreos',
  Stg: 'Santiago',
  '1P': '1 Pedro',
  '2P': '2 Pedro',
  '1Jn': '1 Juan',
  '2Jn': '2 Juan',
  '3Jn': '3 Juan',
  Ap: 'Apocalipsis',
};

// Build a single regex that matches "<book> <chapter>(:<verses>)?"
function buildPattern(): RegExp {
  const allBooks = [...BOOK_NAMES, ...Object.keys(ABBREVIATIONS)]
    .map((b) => b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    // Longer first so "1 Corintios" doesn't get partially matched as "1"
    .sort((a, b) => b.length - a.length)
    .join('|');
  return new RegExp(`\\b(${allBooks})\\s+(\\d{1,3})(?::(\\d{1,3}(?:[-,]\\s*\\d{1,3})*))?\\b`, 'g');
}

const PATTERN = buildPattern();

export interface ScriptureMatch {
  book: string;
  chapter: number;
  verses?: string;
  raw: string;
  start: number;
  end: number;
}

function canonicalBook(raw: string): string {
  const trimmed = raw.trim();
  return ABBREVIATIONS[trimmed] ?? trimmed;
}

export function findScriptureMatches(text: string): ScriptureMatch[] {
  const out: ScriptureMatch[] = [];
  let m: RegExpExecArray | null;
  PATTERN.lastIndex = 0;
  while ((m = PATTERN.exec(text)) !== null) {
    const book = canonicalBook(m[1]!);
    const match: ScriptureMatch = {
      book,
      chapter: parseInt(m[2]!, 10),
      raw: m[0]!,
      start: m.index,
      end: m.index + m[0]!.length,
    };
    if (m[3]) match.verses = m[3].replace(/\s+/g, '');
    out.push(match);
  }
  return out;
}

export function buildScriptureHref(book: string, chapter: number, verses?: string): string {
  const ref = verses ? `${book} ${chapter}:${verses}` : `${book} ${chapter}`;
  return `/portal-v2/scripture?ref=${encodeURIComponent(ref)}`;
}

// ─── Component ─────────────────────────────────────────────────────────────

/**
 * Wraps arbitrary text, detects Bible references, and renders each as a link
 * to the Scripture reader. Refs that point to books not yet bundled in the
 * RV1960 stub still link (the reader will show a "no disponible" message).
 */
export function ScriptureRefText({ children }: { children: string }): JSX.Element {
  const text = children;
  const matches = findScriptureMatches(text);
  if (matches.length === 0) return <>{text}</>;

  const available = new Set(listAvailableBooks().map((b) => b.toLowerCase()));
  const parts: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (m.start > cursor) parts.push(text.slice(cursor, m.start));
    const inStub = available.has(m.book.toLowerCase());
    parts.push(
      <ScriptureLink
        key={`s-${i}`}
        book={m.book}
        chapter={m.chapter}
        {...(m.verses ? { verses: m.verses } : {})}
        raw={m.raw}
        inStub={inStub}
      />,
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

function ScriptureLink({
  book,
  chapter,
  verses,
  raw,
  inStub,
}: {
  book: string;
  chapter: number;
  verses?: string;
  raw: string;
  inStub: boolean;
}): JSX.Element {
  const href = buildScriptureHref(book, chapter, verses);
  return (
    <Tooltip
      title={
        inStub
          ? `Abrir ${book} ${chapter}${verses ? `:${verses}` : ''}`
          : 'Libro aún no disponible en el lector'
      }
      arrow
    >
      <Box
        component={RouterLink}
        to={href}
        sx={{
          color: '#79c0ff',
          textDecoration: 'none',
          borderBottom: '1px dashed rgba(121,192,255,0.4)',
          '&:hover': { borderBottomStyle: 'solid' },
        }}
      >
        {raw}
      </Box>
    </Tooltip>
  );
}
